import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookText, Settings2, Loader2, Plus, Pencil, Power, ShieldX, Info, Lock } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIPO_CUENTA_BADGE = {
  activo: { label: "Activo", cls: "bg-emerald-100 text-emerald-700" },
  pasivo: { label: "Pasivo", cls: "bg-rose-100 text-rose-700" },
  capital: { label: "Capital", cls: "bg-indigo-100 text-indigo-700" },
  resultados: { label: "Resultados", cls: "bg-amber-100 text-amber-700" },
};
const NATURALEZA_LABEL = {
  deudora: "Deudora", acreedora: "Acreedora", orden: "Cuentas de orden",
};
const EMPTY_CUENTA = {
  codigo: "", nombre: "", codigo_agrupador: "",
  tipo_cuenta: "activo", naturaleza: "deudora",
  es_cuenta_mayor: true, clasificacion_jerarquica: "Título",
  cuenta_padre: "", activa: true,
};

export default function ContabilidadFiscal() {
  const { user } = useAuth();
  const location = useLocation();
  const canEdit = user?.role === "admin_entidad";
  const defaultTab = location.pathname.includes("/fiscal/config") ? "config" : "catalogo";

  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [fuente, setFuente] = useState("");
  const [config, setConfig] = useState(null);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_CUENTA);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [cat, cfg] = await Promise.all([
        api.get("/fiscal/catalogo").then((r) => r.data),
        api.get("/fiscal/config").then((r) => r.data),
      ]);
      setCuentas(cat.cuentas || []);
      setFuente(cat.fuente_cita || "");
      setConfig(cfg);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => { setTab(defaultTab); }, [defaultTab]);

  const openNew = () => { setEditId(null); setForm(EMPTY_CUENTA); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      codigo: c.codigo || "", nombre: c.nombre || "", codigo_agrupador: c.codigo_agrupador || "",
      tipo_cuenta: c.tipo_cuenta || "activo",
      naturaleza: c.naturaleza || "deudora",
      es_cuenta_mayor: c.es_cuenta_mayor !== false,
      clasificacion_jerarquica: c.clasificacion_jerarquica || "Título",
      cuenta_padre: c.cuenta_padre || "", activa: c.activa !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) {
      toast.error("Código interno y nombre son obligatorios"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        es_cuenta_mayor: !!form.es_cuenta_mayor,
        cuenta_padre: form.cuenta_padre || null,
      };
      if (editId) await api.put(`/fiscal/catalogo/${editId}`, payload);
      else await api.post("/fiscal/catalogo", payload);
      toast.success(editId ? "Cuenta actualizada" : "Cuenta creada");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActiva = async (c) => {
    try {
      if (c.activa !== false) {
        await api.delete(`/fiscal/catalogo/${c.id}`);
      } else {
        await api.put(`/fiscal/catalogo/${c.id}`, { activa: true });
      }
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.put("/fiscal/config", {
        iva_pct: Number(config.iva_pct),
        isr_tasa_pm: Number(config.isr_tasa_pm),
        coeficiente_utilidad: config.coeficiente_utilidad === "" || config.coeficiente_utilidad === null ? null : Number(config.coeficiente_utilidad),
        comision_apertura_ingreso_en: config.comision_apertura_ingreso_en,
        iva_intereses_regimen: config.iva_intereses_regimen || "gravado",
        integra_sistema_financiero_isr: !!config.integra_sistema_financiero_isr,
      });
      toast.success("Configuración fiscal actualizada");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="fiscal-error">
        <ShieldX className="h-10 w-10 text-red-400" />
        <p className="mt-3 text-sm text-slate-500">No tienes acceso al módulo de Contabilidad y Fiscal.</p>
      </div>
    );
  }

  return (
    <div data-testid="contabilidad-fiscal-page">
      <PageHeader
        title="Contabilidad y Fiscal"
        subtitle="Catálogo de cuentas (Anexo 24 SAT) y configuración fiscal de la SOFOM ENR"
      >
        {!canEdit && (
          <Badge variant="outline" className="gap-1 text-slate-500" data-testid="fiscal-readonly-badge">
            <Lock className="h-3 w-3" /> Solo lectura
          </Badge>
        )}
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="p-6 md:p-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="fiscal-tabs">
              <TabsTrigger value="catalogo" data-testid="tab-catalogo"><BookText className="mr-1.5 h-4 w-4" /> Catálogo de cuentas</TabsTrigger>
              <TabsTrigger value="config" data-testid="tab-config"><Settings2 className="mr-1.5 h-4 w-4" /> Configuración fiscal</TabsTrigger>
            </TabsList>

            {/* ---------------- Catálogo ---------------- */}
            <TabsContent value="catalogo" className="mt-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Info className="h-3.5 w-3.5" /> Fuente: {fuente || "SAT — Anexo 24"}
                </p>
                {canEdit && (
                  <Button data-testid="nueva-cuenta-btn" size="sm" className="bg-primary hover:bg-sidebar-hover" onClick={openNew}>
                    <Plus className="mr-1.5 h-4 w-4" /> Nueva cuenta
                  </Button>
                )}
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <Table data-testid="tabla-catalogo">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Agrupador SAT</TableHead>
                      <TableHead>Tipo de cuenta</TableHead>
                      <TableHead>Naturaleza</TableHead>
                      <TableHead className="text-center">Activa</TableHead>
                      {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cuentas.map((c) => {
                      const tb = TIPO_CUENTA_BADGE[c.tipo_cuenta];
                      return (
                        <TableRow key={c.id} data-testid={`cuenta-row-${c.codigo}`} className={c.activa === false ? "opacity-50" : ""}>
                          <TableCell className="font-mono text-xs tabular-nums">{c.codigo}</TableCell>
                          <TableCell className="text-sm">{c.nombre}</TableCell>
                          <TableCell className="font-mono text-xs tabular-nums">{c.codigo_agrupador || "—"}</TableCell>
                          <TableCell>
                            {tb ? (
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${tb.cls}`}>{tb.label}</span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">{NATURALEZA_LABEL[c.naturaleza] || (c.naturaleza ? String(c.naturaleza) : "—")}</TableCell>
                          <TableCell className="text-center">{c.activa === false ? "No" : "Sí"}</TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <Button data-testid={`edit-cuenta-${c.codigo}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                              <Button data-testid={`toggle-cuenta-${c.codigo}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleActiva(c)}><Power className={`h-4 w-4 ${c.activa === false ? "text-emerald-500" : "text-red-500"}`} /></Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* ---------------- Config fiscal ---------------- */}
            <TabsContent value="config" className="mt-4">
              {config && (
                <div className="max-w-xl rounded-lg border border-slate-200 bg-white p-6" data-testid="form-config-fiscal">
                  <p className="mb-4 text-xs text-slate-500">
                    Régimen de <b>flujo de efectivo</b> (ingresos cobrados). Versión actual: v{config.version ?? 1}
                    {config.updated_by ? ` · última edición: ${config.updated_by}` : ""}.
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label>IVA (%)</Label>
                      <Input data-testid="cfg-iva" type="number" value={config.iva_pct ?? ""} disabled={!canEdit}
                        onChange={(e) => setConfig({ ...config, iva_pct: e.target.value })} />
                    </div>
                    <div>
                      <Label>Tasa ISR PM (%)</Label>
                      <Input data-testid="cfg-isr" type="number" value={config.isr_tasa_pm ?? ""} disabled={!canEdit}
                        onChange={(e) => setConfig({ ...config, isr_tasa_pm: e.target.value })} />
                    </div>
                    <div>
                      <Label>Coeficiente de utilidad</Label>
                      <Input data-testid="cfg-coef" type="number" step="0.0001" value={config.coeficiente_utilidad ?? ""} disabled={!canEdit}
                        placeholder="Opcional" onChange={(e) => setConfig({ ...config, coeficiente_utilidad: e.target.value })} />
                    </div>
                    <div>
                      <Label>Comisión de apertura — ingreso en</Label>
                      <Select value={config.comision_apertura_ingreso_en || "disposicion"} disabled={!canEdit}
                        onValueChange={(v) => setConfig({ ...config, comision_apertura_ingreso_en: v })}>
                        <SelectTrigger data-testid="cfg-comision"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disposicion">Al disponer</SelectItem>
                          <SelectItem value="cobro">Al cobrar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600" data-testid="cfg-moratorio-iva-info">
                      <b>IVA de los intereses moratorios:</b> sigue automáticamente el régimen de IVA del crédito
                      (Art. 15-X LIVA) — crédito gravado → moratorio con IVA; crédito exento → moratorio exento.
                      El importe exacto lo sella el motor de pagos en cada cobro; ya no existe un switch global editable.
                    </div>
                  </div>

                  {/* ============ Régimen fiscal para IVA sobre INTERESES ============ */}
                  <div className="mt-6 rounded-md border border-primary/25 bg-primary/5 p-4" data-testid="cfg-iva-intereses-panel">
                    <div className="mb-3 flex items-start gap-2">
                      <Info className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                      <div className="text-xs text-slate-700 leading-relaxed">
                        <p className="font-semibold text-primary mb-1">¿Cuándo van EXENTOS los intereses de IVA?</p>
                        <p>Generalmente cuando la SOFOM integra el sistema financiero para efectos de la LISR (por su nivel de operaciones de crédito). También suelen estar exentos los intereses de créditos destinados a: <b>vivienda (casa habitación)</b>, y créditos otorgados a <b>personas físicas para actividades empresariales, agrícolas, ganaderas o pesqueras</b>, entre otros supuestos del <b>Art. 15 de la Ley del IVA</b>.</p>
                        <p className="font-semibold text-primary mt-2 mb-1">¿Cuándo van GRAVADOS con IVA 16%?</p>
                        <p>Generalmente cuando la SOFOM <b>no</b> integra el sistema financiero para efectos de la LISR, y el crédito se otorga para consumo o destinos no exentos (por ejemplo, créditos de consumo a personas físicas que no realizan actividad empresarial).</p>
                        <p className="mt-2 rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] text-amber-900">
                          <b>Importante:</b> esta clasificación depende de la situación fiscal específica de tu entidad y del destino del crédito. Confirma el tratamiento con tu <b>contador o asesor fiscal</b>. Esta configuración determina cómo se calcula el IVA en las tablas de amortización, pagos y pólizas.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-2">
                      <div className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3">
                        <div>
                          <Label className="cursor-pointer" htmlFor="cfg-integra-sf">¿La SOFOM integra el sistema financiero para LISR?</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">Solo referencia — sugiere el tratamiento por defecto</p>
                        </div>
                        <Switch data-testid="cfg-integra-sf" id="cfg-integra-sf" checked={!!config.integra_sistema_financiero_isr}
                          disabled={!canEdit}
                          onCheckedChange={(v) => {
                            setConfig((prev) => {
                              const next = { ...prev, integra_sistema_financiero_isr: v };
                              // Sugerencia (no fuerza): si el usuario no cambió aún el régimen, aliniamos.
                              if (!prev._regimen_manual) {
                                next.iva_intereses_regimen = v ? "exento" : "gravado";
                              }
                              return next;
                            });
                          }} />
                      </div>

                      <div>
                        <Label>Tratamiento de IVA sobre intereses</Label>
                        <Select value={config.iva_intereses_regimen || "gravado"} disabled={!canEdit}
                          onValueChange={(v) => setConfig({ ...config, iva_intereses_regimen: v, _regimen_manual: true })}>
                          <SelectTrigger data-testid="cfg-iva-intereses-regimen"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gravado">Gravado 16% (interés lleva IVA)</SelectItem>
                            <SelectItem value="exento">Exento (interés SIN IVA)</SelectItem>
                          </SelectContent>
                        </Select>
                        {config.integra_sistema_financiero_isr && config.iva_intereses_regimen === "gravado" && (
                          <p className="mt-1 text-[11px] text-amber-700" data-testid="cfg-iva-hint-integra">
                            Marcaste que la SOFOM integra el sistema financiero — normalmente el tratamiento sería <b>Exento</b>. Confirma con tu contador si eliges Gravado.
                          </p>
                        )}
                        {!config.integra_sistema_financiero_isr && config.iva_intereses_regimen === "exento" && (
                          <p className="mt-1 text-[11px] text-amber-700" data-testid="cfg-iva-hint-nointegra">
                            La SOFOM no integra el sistema financiero — normalmente el interés estaría <b>Gravado</b>. Puedes seleccionar Exento si el destino del crédito lo justifica (vivienda, actividad empresarial de PF, etc.).
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="mt-5 flex justify-end">
                      <Button data-testid="guardar-config-btn" className="bg-primary hover:bg-sidebar-hover" onClick={saveConfig} disabled={busy}>
                        {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar configuración
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Diálogo crear/editar cuenta */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="cuenta-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar cuenta" : "Nueva cuenta"}</DialogTitle>
            <DialogDescription>Define la cuenta y su código agrupador del SAT (Anexo 24).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Código interno</Label>
              <Input data-testid="cuenta-codigo" value={form.codigo} disabled={!!editId}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
            </div>
            <div>
              <Label>Código agrupador SAT</Label>
              <Input data-testid="cuenta-agrupador" value={form.codigo_agrupador}
                onChange={(e) => setForm({ ...form, codigo_agrupador: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Nombre</Label>
              <Input data-testid="cuenta-nombre" value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            </div>
            <div>
              <Label>Tipo de cuenta</Label>
              <Select value={form.tipo_cuenta} onValueChange={(v) => setForm({ ...form, tipo_cuenta: v })}>
                <SelectTrigger data-testid="cuenta-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="pasivo">Pasivo</SelectItem>
                  <SelectItem value="capital">Capital</SelectItem>
                  <SelectItem value="resultados">Resultados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Naturaleza</Label>
              <Select value={form.naturaleza} onValueChange={(v) => setForm({ ...form, naturaleza: v })}>
                <SelectTrigger data-testid="cuenta-naturaleza"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deudora">Deudora</SelectItem>
                  <SelectItem value="acreedora">Acreedora</SelectItem>
                  <SelectItem value="orden">Cuentas de orden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-100 p-3">
              <Label className="cursor-pointer text-sm">¿Cuenta de mayor?</Label>
              <Switch data-testid="cuenta-es-mayor" checked={!!form.es_cuenta_mayor}
                onCheckedChange={(v) => setForm({ ...form, es_cuenta_mayor: v })} />
            </div>
            <div>
              <Label>Título / Subtítulo</Label>
              <Select value={form.clasificacion_jerarquica} onValueChange={(v) => setForm({ ...form, clasificacion_jerarquica: v })}>
                <SelectTrigger data-testid="cuenta-clasificacion"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Título">Título</SelectItem>
                  <SelectItem value="Subtítulo">Subtítulo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Cuenta padre (código)</Label>
              <Input data-testid="cuenta-padre" value={form.cuenta_padre || ""}
                onChange={(e) => setForm({ ...form, cuenta_padre: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="guardar-cuenta-btn" className="bg-primary hover:bg-sidebar-hover" onClick={save} disabled={busy}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
